import React, {useEffect, useRef, useState} from 'react';
import './ShopPage.css';
import PageButton from '../../elements/PageButton/PageButton';
import {FACTION_CONFIGS, FACTION_CONFIGS_IMPLEMENTED} from '../../../configs/factions';
import {SHOP_CHEST_CONFIGS} from '../../../configs/chests';
import {CHESTS} from '../../../constants/chests';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {getFactionImagePath} from "../../../utils/factions/getFactionImagePath";
import {LoadedImage} from "../../EmblaCarousel/FactionsCarousel";
import FactionCardOverlay from "../../FactionCardOverlay/FactionCardOverlay";
import FactionPurchaseRewardModal from "../../Modals/FactionPurchaseRewardModal/FactionPurchaseRewardModal";
import ChestPurchaseModal from "../../Modals/ChestPurchaseModal/ChestPurchaseModal";
import NotEnoughCrystalsModal from "./NotEnoughCrystalsModal/NotEnoughCrystalsModal";
import CrystalsExchangeModal from "../../Modals/CrystalsExchangeModal/CrystalsExchangeModal";
import ShopChestsTab from "./ShopChestsTab/ShopChestsTab";
import ShopPremiumTab from "./ShopPremiumTab/ShopPremiumTab";
import ShopCrystalsTab from "./ShopCrystalsTab/ShopCrystalsTab";
import {MENU_SOUND_MANAGER} from "../../../scenes/MenuSoundManager";
import UserHeader from "../../UserHeader/UserHeader";
import {getShopTabsTopStyle} from "./getShopTabsTopStyle";
import {getFormattedPrice} from "../../../utils/price/getFormattedPrice";
import {useSelector} from "react-redux";
import {API_BASE} from "../../../utils/apiConfig";
import {getCachedOrFetch} from "../../../utils/fetchCache";
import {useFactionPurchase} from "../../../hooks/factions/useFactionPurchase";
import {useChestPurchase} from "../../../hooks/shop/useChestPurchase";

// Экспортируется — App.js ссылается на TABS.CRYSTALS при переходе в магазин
// «за кристаллами» из мест за пределами ShopPage (UserHeader на MainMenu/FactionPage)
export const TABS = {
  FACTIONS: 'factions',
  CHESTS: 'chests',
  PREMIUM: 'premium',
  CRYSTALS: 'crystals',
};

// Экспортируется — MainMenu.js предзагружает эти иконки заранее (см.
// SHOP_TAB_ICON_PATHS), они же плейсхолдеры плашек товаров в самом магазине
export const TAB_CONFIG = [
  {id: TABS.CHESTS, icon: '/img/icons/chest.png', title: 'Сундуки'},
  {id: TABS.FACTIONS, icon: '/img/icons/banner.png', title: 'Фракции'},
  {id: TABS.PREMIUM, icon: '/img/icons/premium.png', title: 'Премиум'},
  {id: TABS.CRYSTALS, icon: '/img/icons/crystal.png', title: 'Кристаллы'},
];

export const SHOP_TAB_ICON_PATHS = TAB_CONFIG.map((tab) => tab.icon);

// Скидка на сундуках — оптовая (x10, discountPercentsFor10ItemsPurchase),
// не привязана к владению (в отличие от фракций — сундук можно покупать
// многократно), поэтому достаточно статической проверки конфигов
const HAS_CHEST_X10_DISCOUNT = SHOP_CHEST_CONFIGS.some(
  (chest) => (chest.discountPercentsFor10ItemsPurchase || 0) > 0
);

const PAGE_ID = 'ShopPage';

function ShopTabButton({tabId, icon, title, isActive, onClick, showDiscountBadge}) {
  return (
    <PageButton
      className={`shop-tab-button ${isActive ? 'shop-tab-button_active' : ''}`}
      onClick={onClick}
      pageId={PAGE_ID}
      buttonId={`tab_${title}`}
      title={title}
    >
      {tabId === TABS.FACTIONS
        // Иконка фракций — плоское знамя (banner.png), на фоне вкладки сливалась
        // в выбранном состоянии: маска + background-color вместо img дают
        // плавный переход цвета при селекте/деселекте (тот же прием, что и
        // у shop-chest-eternal-banner-icon для этого же banner.png)
        ? <span className="shop-tab-icon shop-tab-icon_factions" role="img" aria-label={title}/>
        : <img className="shop-tab-icon" src={icon} alt={title}/>}
      {showDiscountBadge && (
        <img
          className="shop-coupon-icon shop-tab-discount-badge"
          src="/img/icons/coupon.png"
          alt="Скидка"
        />
      )}
    </PageButton>
  );
}

export function ShopFactionBuyButton({
                                faction,
                                onBuy,
                                isOwned = false,
                                isInDevelopment = false,
                                isOverlayMode = false,
                                buttonIdSuffix = '',
                                       pageId = PAGE_ID,
                                       // Префикс «Купить за» рассчитан на широкую кнопку в
                                       // FactionCardOverlay — по умолчанию следует isOverlayMode
                                       // (сохраняет прежнее поведение там), но в узкой кнопке
                                       // карусели (между стрелками) он один переполняет строку
                                       // и раздувает кнопку — там передают showBuyPrefix={false}
                                       showBuyPrefix = isOverlayMode,
                              }) {
  if (isInDevelopment) {
    return (
      <PageButton
        className={isOverlayMode ? 'shop-faction-buy-button-overlay-mode' : 'shop-faction-buy-button'}
        onClick={onBuy}
        pageId={pageId}
        buttonId={`buy${buttonIdSuffix}_${faction.id}`}
        title={`Фракция ${faction.name} в разработке`}
        noSound={isInDevelopment}
      >
        В разработке
      </PageButton>
    );
  }

  if (isOwned) {
    return (
      <PageButton
        className={isOverlayMode ? 'shop-faction-buy-button-overlay-mode' : 'shop-faction-buy-button'}
        onClick={onBuy}
        pageId={pageId}
        buttonId={`buy${buttonIdSuffix}_${faction.id}`}
        title={`Фракция ${faction.name} уже открыта`}
      >
        Уже открыта
      </PageButton>
    );
  }

  const hasCoupon = faction.originalPriceCrystals != null && faction.priceCrystals !== faction.originalPriceCrystals;

  return (
    <PageButton
      className={isOverlayMode ? 'shop-faction-buy-button-overlay-mode' : 'shop-faction-buy-button'}
      onClick={onBuy}
      pageId={pageId}
      buttonId={`buy${buttonIdSuffix}_${faction.id}`}
      title={`Купить фракцию ${faction.name}`}
    >
      <>
        {showBuyPrefix && (
          <span>
              Купить за
            </span>
        )}
        {hasCoupon && (
          <>
            <img className="shop-coupon-icon" src="/img/icons/coupon.png" alt="Скидка"/>
            <span className="shop-faction-buy-button-original-price">
              {getFormattedPrice(faction.originalPriceCrystals)}
            </span>
          </>
        )}
        <span
          className={'shop-faction-buy-button-overlay-mode_price'}
        >
          {getFormattedPrice(faction.priceCrystals)}
          <img className="shop-faction-price-icon" src="/img/icons/crystal.png" alt="Кристаллы"/>
        </span>
      </>
    </PageButton>
  );
}

function ShopFactionItem({faction, onClick, onBuy, isOwned = false, isInDevelopment = false}) {
  return (
    <div
      className={`shop-faction-item${isOwned ? ' shop-faction-item_owned' : ''}${isInDevelopment ? ' shop-faction-item_in-development' : ''}`}
      onClick={onClick}
    >
      <LoadedImage
        className="shop-faction-icon"
        src={getFactionImagePath(faction.id)}
        alt={faction.name}
        placeholderSrc="/img/icons/banner.png"
      />
      <div
        className="shop-faction-name"
        style={{color: faction.tempColor}}
      >
        {faction.name}
        {/*<img*/}
        {/*  className="shop-faction-info-icon"*/}
        {/*  src={'/img/icons/info.png'}*/}
        {/*  alt={faction.name}*/}
        {/*  title={`Подробно о фракции ${faction.name}`}*/}
        {/*/>*/}
      </div>

      <ShopFactionBuyButton
        faction={faction}
        isOwned={isOwned}
        isInDevelopment={isInDevelopment}
        onBuy={(event) => {
          event.stopPropagation();
          if (isOwned) {
            onClick?.();
          } else {
            onBuy?.(faction.id);
          }
        }}
      />
    </div>
  );
}

function ShopFactionsTab({availableFactions, onFactionClick, onBuyFaction, onDevelopmentClick}) {
  const ownedFactionIds = new Set(availableFactions.filter((f) => f.isAvailable).map((f) => f.id));
  const unownedFactions = FACTION_CONFIGS_IMPLEMENTED.filter((faction) => !ownedFactionIds.has(faction.id));
  const inDevelopmentFactions = FACTION_CONFIGS.filter((faction) => !faction.implemented);
  const ownedFactions = FACTION_CONFIGS.filter((faction) => ownedFactionIds.has(faction.id));

  const handleFactionClick = (faction) => {
    MENU_SOUND_MANAGER.playFactionSelect(faction.id, true);
    onFactionClick?.(faction.id);
  };

  const handleDevelopmentClick = () => {
    MENU_SOUND_MANAGER.playChestEternal();
    onDevelopmentClick?.();
  };

  return (
    <div className="shop-tab-content">
      <div className="shop-factions-grid">
        {unownedFactions.map((faction) => (
          <ShopFactionItem
            key={faction.id}
            faction={faction}
            isOwned={false}
            isInDevelopment={false}
            onClick={() => handleFactionClick(faction)}
            onBuy={onBuyFaction}
          />
        ))}
        {inDevelopmentFactions.map((faction) => (
          <ShopFactionItem
            key={faction.id}
            faction={faction}
            isOwned={false}
            isInDevelopment={true}
            onClick={handleDevelopmentClick}
            onBuy={handleDevelopmentClick}
          />
        ))}
        {ownedFactions.map((faction) => (
          <ShopFactionItem
            key={faction.id}
            faction={faction}
            isOwned={true}
            isInDevelopment={false}
            onClick={() => handleFactionClick(faction)}
            onBuy={onBuyFaction}
          />
        ))}
      </div>
    </div>
  );
}

function ShopPage({onBack, onOpenInventory, initialTab}) {
  const auth = useSelector((state) => state.private.auth);
  const [activeTab, setActiveTab] = useState(initialTab || TABS.CHESTS);
  const [overlayFactionId, setOverlayFactionId] = useState(null);
  // {chestId, initialQuantity} — initialQuantity предзаполняет поле количества
  // в модалке (кнопка «x10» на плашке открывает модалку сразу с 10 шт.)
  const [chestForPurchase, setChestForPurchase] = useState(null);
  const [ownedChestIds, setOwnedChestIds] = useState([]);
  usePageAnalytics('ShopPage');

  // Позиция табов при скролле и отступ под системный статус-бар —
  // см. getShopTabsTopStyle.
  // .shop-page фактически не ограничен высотой вьюпорта (height:100% не
  // резолвится в определенную высоту без явно заданной высоты у предков) —
  // .shop-content, несмотря на overflow-y:auto, никогда сам не переполняется
  // и не скроллится: скроллится ДОКУМЕНТ (window), поэтому слушаем именно
  // его, а не scroll самого .shop-content. Позиция меняется напрямую через
  // ref (не через state) — это скроллиный обработчик, ре-рендер на каждый
  // тик скролла был бы лишним. Эффект зависит от activeTab только чтобы
  // вернуть табы в исходную позицию при смене вкладки
  const shopTabsRef = useRef(null);
  useEffect(() => {
    const tabsEl = shopTabsRef.current;
    if (!tabsEl) return;

    const handleScroll = () => {
      tabsEl.style.top = getShopTabsTopStyle(window.scrollY);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeTab]);

  const availableFactions = auth.availableFactions || [];
  const isEternalOpened = Boolean(auth.isAllFactionsAvailable);
  const isEternalOwned = !isEternalOpened && ownedChestIds.includes(CHESTS.ETERNAL);

  // В отличие от сундуков, скидка на фракцию актуальна только пока она не
  // куплена — бейдж скидки на табе не должен маячить, если все фракции со
  // скидкой у игрока уже открыты
  const ownedFactionIds = new Set(availableFactions.filter((f) => f.isAvailable).map((f) => f.id));
  const hasUnownedDiscountedFactions = FACTION_CONFIGS_IMPLEMENTED.some(
    (faction) => !ownedFactionIds.has(faction.id)
      && faction.originalPriceCrystals != null
      && faction.priceCrystals !== faction.originalPriceCrystals
  );

  const {
    handleBuyFaction,
    purchasedFactionId,
    setPurchasedFactionId,
    notEnoughCrystalsAmount,
    setNotEnoughCrystalsAmount,
  } = useFactionPurchase(auth, availableFactions);

  const {
    handleBuyChest,
    notEnoughCrystalsAmount: chestNotEnoughCrystalsAmount,
    setNotEnoughCrystalsAmount: setChestNotEnoughCrystalsAmount,
    missingNumensAmount,
    setMissingNumensAmount,
  } = useChestPurchase(auth, (chestId, quantity) => {
    setOwnedChestIds((prev) => [...prev, ...Array(quantity).fill(chestId)]);
  });

  useEffect(() => {
    let cancelled = false;

    const fetchInventory = async () => {
      try {
        const data = await getCachedOrFetch('inventory', () => fetch(`${API_BASE}/inventory`, {
          method: 'GET',
          credentials: 'include',
        }).then(async (res) => {
          if (!res.ok) throw new Error('Failed to fetch inventory');
          return res.json();
        }));
        if (!cancelled) {
          setOwnedChestIds(data.inventory?.chests || []);
        }
      } catch (e) {
        console.error('Shop inventory fetch error', e);
      }
    };

    fetchInventory();
    return () => {
      cancelled = true;
    };
  }, []);

  const openChestPurchase = (chestId, initialQuantity = 1) => {
    setChestForPurchase({chestId, initialQuantity});
  };

  const renderContent = () => {
    switch (activeTab) {
      case TABS.FACTIONS:
        return (
          <ShopFactionsTab
            availableFactions={availableFactions}
            onFactionClick={setOverlayFactionId}
            onBuyFaction={handleBuyFaction}
            onDevelopmentClick={() => openChestPurchase(CHESTS.ETERNAL)}
          />
        );
      case TABS.PREMIUM:
        return <ShopPremiumTab/>;
      case TABS.CRYSTALS:
        return <ShopCrystalsTab/>;
      case TABS.CHESTS:
        return (
          <ShopChestsTab
            onBuyChestClick={openChestPurchase}
            isEternalOwned={isEternalOwned}
            isEternalOpened={isEternalOpened}
            onOpenInventory={onOpenInventory}
          />
        );
      default:
        return (
          <ShopFactionsTab
            availableFactions={availableFactions}
            onFactionClick={setOverlayFactionId}
            onBuyFaction={handleBuyFaction}
            onDevelopmentClick={() => openChestPurchase(CHESTS.ETERNAL)}
          />
        );
    }
  };

  const onFactionCardOverlayClose = () => {
    setOverlayFactionId(null)
  }
  return (
    <div className="shop-page">
      <PageButton
        className="main-menu-button shop-floating-back-button"
        onClick={onBack}
        pageId={PAGE_ID}
        buttonId="back_floating"
      >
        <img
          className="shop-floating-back-button_icon"
          src="/img/icons/back_navigation.png"
          alt="Назад"
          title="Назад"
        />
      </PageButton>

      <UserHeader pageId={'ShopPage'} onGoToCrystals={() => setActiveTab(TABS.CRYSTALS)}/>

      <div className="shop-tabs" ref={shopTabsRef}>
        {TAB_CONFIG.map((tab) => (
          <ShopTabButton
            key={tab.id}
            tabId={tab.id}
            icon={tab.icon}
            title={tab.title}
            isActive={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            showDiscountBadge={
              (tab.id === TABS.FACTIONS && hasUnownedDiscountedFactions)
              || (tab.id === TABS.CHESTS && HAS_CHEST_X10_DISCOUNT)
            }
          />
        ))}
      </div>

      <div className="shop-content" key={activeTab}>
        {renderContent()}
      </div>

      {overlayFactionId && (
        <FactionCardOverlay
          factionId={overlayFactionId}
          onClose={onFactionCardOverlayClose}
          renderBuyButton={(faction) => {
            const isOwned = availableFactions.some((f) => f.id === faction.id && f.isAvailable);
            const isInDevelopment = !faction.implemented;

            const onBuy = () => {
              onFactionCardOverlayClose();
              return handleBuyFaction(faction.id);
            }

            if (isInDevelopment) {
              return (
                <ShopFactionBuyButton
                  faction={faction}
                  isInDevelopment={true}
                  buttonIdSuffix="_overlay"
                  isOverlayMode={true}
                  onBuy={() => {
                    onFactionCardOverlayClose();
                    openChestPurchase(CHESTS.ETERNAL);
                  }}
                />
              );
            }

            return (
              <ShopFactionBuyButton
                faction={faction}
                isOwned={isOwned}
                buttonIdSuffix="_overlay"
                isOverlayMode={true}
                onBuy={isOwned ? undefined : onBuy}
              />
            );
          }}
        />
      )}

      {purchasedFactionId && (
        <FactionPurchaseRewardModal
          factionId={purchasedFactionId}
          onClaim={() => setPurchasedFactionId(null)}
        />
      )}

      {chestForPurchase && (
        <ChestPurchaseModal
          chest={SHOP_CHEST_CONFIGS.find((c) => c.id === chestForPurchase.chestId)}
          onClose={() => setChestForPurchase(null)}
          onPurchase={handleBuyChest}
          onOpenInventory={onOpenInventory}
          initialQuantity={chestForPurchase.initialQuantity}
          alreadyOwned={chestForPurchase.chestId === CHESTS.ETERNAL && isEternalOpened}
        />
      )}

      {(notEnoughCrystalsAmount != null || chestNotEnoughCrystalsAmount != null) && (
        <NotEnoughCrystalsModal
          missingAmount={notEnoughCrystalsAmount ?? chestNotEnoughCrystalsAmount}
          onGoToCrystals={() => {
            setNotEnoughCrystalsAmount(null);
            setChestNotEnoughCrystalsAmount(null);
            setChestForPurchase(null);
            setOverlayFactionId(null);
            setActiveTab(TABS.CRYSTALS);
          }}
          onClose={() => {
            setNotEnoughCrystalsAmount(null);
            setChestNotEnoughCrystalsAmount(null);
          }}
        />
      )}

      {missingNumensAmount != null && (
        <CrystalsExchangeModal
          missingNumensAmount={missingNumensAmount}
          onClose={() => setMissingNumensAmount(null)}
          onGoToCrystals={() => {
            setMissingNumensAmount(null);
            setChestForPurchase(null);
            setOverlayFactionId(null);
            setActiveTab(TABS.CRYSTALS);
          }}
        />
      )}
    </div>
  );
}

export default ShopPage;
